import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

const emptyParent = { name: '', phone: '', email: '' };

const generateParentPassword = (name) => {
  if (!name) return 'Parent@1234';
  const cleanName = String(name).replace(/[^a-zA-Z]/g, '').toUpperCase();
  const namePart = cleanName.slice(0, 4).padEnd(4, 'X');

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const dobPart = `${day}${month}`;

  return `${namePart}${dobPart}`;
};

const countryOptions = [
  { code: '+27', flag: '🇿🇦', name: 'South Africa (+27)' },
  { code: '+91', flag: '🇮🇳', name: 'India (+91)' },
  { code: '+1', flag: '🇺🇸', name: 'USA (+1)' },
  { code: '+44', flag: '🇬🇧', name: 'UK (+44)' },
  { code: '+971', flag: '🇦🇪', name: 'UAE (+971)' },
  { code: '+61', flag: '🇦🇺', name: 'Australia (+61)' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore (+65)' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia (+966)' },
];

export default function ParentsPage() {
  const navigate = useNavigate();
  const { currentBranch, user } = useAuth();
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('a-z');

  // Link Student Modal Branch/Class filter state
  const [linkBranchId, setLinkBranchId] = useState('');
  const [linkClassId, setLinkClassId] = useState('');
  const [linkBranchClasses, setLinkBranchClasses] = useState([]);
  const [linkClassesLoading, setLinkClassesLoading] = useState(false);

  const [form, setForm] = useState(emptyParent);
  const [countryCode, setCountryCode] = useState('+27');
  const [editingParent, setEditingParent] = useState(null);
  const [isAddParentOpen, setIsAddParentOpen] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [mapping, setMapping] = useState({ student_id: '', parent_id: '', parent_name: '', relation: 'Parent', is_primary: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [currentBranch]);

  // Fetch branch classes when linkBranchId changes
  useEffect(() => {
    if (linkBranchId) {
      fetchBranchClassesForLinkModal(linkBranchId);
    } else {
      setLinkBranchClasses([]);
      setLinkClassId('');
    }
  }, [linkBranchId]);

  async function fetchBranchClassesForLinkModal(bId) {
    setLinkClassesLoading(true);
    try {
      const res = await api.get(`/branches/${bId}/classes`);
      setLinkBranchClasses((res.data.data || []).filter(c => c.is_active === 1));
    } catch (err) {
      setLinkBranchClasses([]);
    } finally {
      setLinkClassesLoading(false);
    }
  }

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const userParams = { role: 'parent' };
      const studentParams = {};
      if (currentBranch) {
        userParams.branch_id = currentBranch.branch_id;
        studentParams.branch_id = currentBranch.branch_id;
      }

      const [parentResponse, studentResponse, branchResponse] = await Promise.all([
        api.get('/users', { params: userParams }),
        api.get('/students', { params: studentParams }),
        api.get('/admin/branches')
      ]);
      setParents(parentResponse.data.data || []);
      setStudents(studentResponse.data.data || []);
      setBranches(branchResponse.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load parents');
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setEditingParent(null);
    setForm(emptyParent);
    setCountryCode('+27');
    setIsAddParentOpen(true);
  };

  const openEditModal = (parent) => {
    setEditingParent(parent);
    
    let rawPhone = parent.phone || '';
    let foundCode = '+27';
    let digitsOnly = rawPhone.replace(/\D/g, '');

    for (const option of countryOptions) {
      const cCodeDigits = option.code.replace('+', '');
      if (rawPhone.startsWith(option.code) || digitsOnly.startsWith(cCodeDigits)) {
        foundCode = option.code;
        if (digitsOnly.startsWith(cCodeDigits)) {
          digitsOnly = digitsOnly.slice(cCodeDigits.length);
        }
        break;
      }
    }

    setCountryCode(foundCode);
    setForm({
      name: parent.name || '',
      phone: digitsOnly.slice(0, 10),
      email: parent.email || '',
    });
    setIsAddParentOpen(true);
  };

  const openLinkModal = (parent) => {
    setMapping({
      student_id: '',
      parent_id: parent.id,
      parent_name: parent.name,
      relation: 'Parent',
      is_primary: true,
    });
    setStudentSearchQuery('');

    // Pre-select manager's current branch automatically
    const initialBranchId = currentBranch?.branch_id ? String(currentBranch.branch_id) : '';
    setLinkBranchId(initialBranchId);
    setLinkClassId('');
    setIsLinkOpen(true);
  };

  const closeModal = () => {
    setIsAddParentOpen(false);
    setEditingParent(null);
    setForm(emptyParent);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    let finalPhone = '';
    if (form.phone) {
      const cleanPhone = form.phone.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile phone number');
        return;
      }
      finalPhone = `${countryCode}${cleanPhone}`;
    }

    try {
      const payload = {
        name: form.name.trim(),
        phone: finalPhone || undefined,
        email: form.email ? form.email.trim() : undefined,
      };

      if (editingParent) {
        await api.put(`/users/${editingParent.id}`, payload);
        setSuccessMessage('Parent details updated successfully');
      } else {
        const autoPassword = generateParentPassword(form.name);
        await api.post('/users', {
          ...payload,
          role: 'parent',
          password: autoPassword,
        });
        setSuccessMessage('Parent added successfully');
      }

      closeModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save parent');
    }
  };

  const linkParent = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!mapping.student_id) {
      setError('Please select a student to link');
      return;
    }

    try {
      await api.post(`/students/${mapping.student_id}/parents`, {
        parent_id: mapping.parent_id,
        relation: mapping.relation,
        is_primary: mapping.is_primary,
      });
      const selectedStudent = students.find(s => String(s.id) === String(mapping.student_id));
      const studentName = selectedStudent ? selectedStudent.full_name : 'Student';
      setSuccessMessage(`Parent "${mapping.parent_name}" linked with "${studentName}" successfully`);
      setMapping({ student_id: '', parent_id: '', parent_name: '', relation: 'Parent', is_primary: true });
      setIsLinkOpen(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to link parent');
    }
  };

  const filteredStudentsForLink = students.filter((s) => {
    if (linkBranchId && String(s.branch_id) !== String(linkBranchId)) {
      return false;
    }
    if (linkClassId) {
      const selectedClassObj = linkBranchClasses.find(c => String(c.id) === String(linkClassId));
      const selectedClassName = selectedClassObj ? (selectedClassObj.section ? `${selectedClassObj.name} (${selectedClassObj.section})` : selectedClassObj.name) : '';
      const selectedClassRawName = selectedClassObj ? selectedClassObj.name : '';

      const matchById = s.class_id && String(s.class_id) === String(linkClassId);
      const matchByName = s.class_grade && (
        (selectedClassName && String(s.class_grade).trim().toLowerCase() === selectedClassName.trim().toLowerCase()) ||
        (selectedClassRawName && String(s.class_grade).trim().toLowerCase() === selectedClassRawName.trim().toLowerCase())
      );

      if (!matchById && !matchByName) {
        return false;
      }
    }
    if (studentSearchQuery.trim()) {
      const q = studentSearchQuery.toLowerCase().trim();
      const name = String(s.full_name || '').toLowerCase();
      const stId = String(s.student_id || '').toLowerCase();
      const classGrade = String(s.class_grade || '').toLowerCase();
      return name.includes(q) || stId.includes(q) || classGrade.includes(q);
    }
    return true;
  });

  const handleUnlinkStudent = async (studentId, parentId, studentName, parentName) => {
    if (!window.confirm(`Are you sure you want to unlink student "${studentName}" from parent "${parentName}"?`)) {
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      await api.delete(`/students/${studentId}/parents/${parentId}`);
      setSuccessMessage(`Unlinked "${studentName}" from "${parentName}" successfully`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to unlink student');
    }
  };

  const sortedParents = sortItems(parents, sortOption, 'name', 'created_at');

  const filteredParents = sortedParents.filter((p) => {
    if (!parentSearchQuery.trim()) return true;
    const q = parentSearchQuery.toLowerCase().trim();
    const matchName = p.name?.toLowerCase().includes(q);
    const matchPhone = p.phone?.toLowerCase().includes(q);
    const matchEmail = p.email?.toLowerCase().includes(q);
    const matchStudent = p.linked_students?.some(s =>
      s.full_name?.toLowerCase().includes(q) || s.student_id?.toLowerCase().includes(q)
    );
    return matchName || matchPhone || matchEmail || matchStudent;
  });

  return (
    <div>
      <PageHeader
        title="Parent Management"
        subtitle="Add parents and link them with child profiles"
        action={
          <button type="button" className="primary-button" onClick={openAddModal} style={{ whiteSpace: 'nowrap' }}>
            + Add Parent
          </button>
        }
      />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search parent by name, phone, email..."
            value={parentSearchQuery}
            onChange={(e) => setParentSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px 8px 34px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 14,
              minWidth: 260,
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              background: '#ffffff'
            }}
          />
          <span style={{ position: 'absolute', left: 10, color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>
            🔍
          </span>
          {parentSearchQuery && (
            <button
              type="button"
              onClick={() => setParentSearchQuery('')}
              style={{
                position: 'absolute',
                right: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              ×
            </button>
          )}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <Modal isOpen={isAddParentOpen} onClose={closeModal} title={editingParent ? 'Edit Parent' : 'Add Parent'}>
        <form onSubmit={handleSubmit}>
          <label>
            Name
            <input placeholder="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          </label>
          <label>
            Phone Number (10 digits)
            <div style={{ display: 'flex', gap: 6 }}>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ width: 110, padding: '8px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 13, background: '#f8fafc', cursor: 'pointer' }}
              >
                {countryOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                maxLength={10}
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                style={{ flex: 1 }}
              />
            </div>
            {form.phone && (
              <span style={{ fontSize: 11, color: form.phone.length === 10 ? '#16a34a' : '#dc2626', marginTop: 3, display: 'block', fontWeight: 500 }}>
                {form.phone.length === 10 ? '✓ Valid 10-digit number' : `Entered ${form.phone.length}/10 digits`}
              </span>
            )}
          </label>
          <label>
            Email
            <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          </label>

          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={closeModal}>Cancel</button>
            <button type="submit" className="primary-button">{editingParent ? 'Update Parent' : 'Save Parent'}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isLinkOpen} onClose={() => setIsLinkOpen(false)} title={`Link Student for "${mapping.parent_name}"`}>
        <form onSubmit={linkParent}>
          <label>
            Parent Name
            <input
              type="text"
              value={mapping.parent_name || ''}
              disabled
              style={{ background: '#f1f5f9', color: '#475569', fontWeight: 600, cursor: 'not-allowed' }}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px', marginBottom: '8px' }}>
            <label style={{ margin: 0 }}>
              {user?.role === 'admin' ? 'Filter by Branch (Optional)' : 'Branch'}
              {user?.role === 'admin' ? (
                <select
                  value={linkBranchId}
                  onChange={(e) => {
                    setLinkBranchId(e.target.value);
                    setLinkClassId('');
                  }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4, background: '#fff', cursor: 'pointer' }}
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              ) : (
                <div style={{
                  padding: '8px 12px',
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '7px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#334155',
                  marginTop: 4
                }}>
                  📍 {currentBranch?.name || 'Current Branch'}
                </div>
              )}
            </label>

            <label style={{ margin: 0 }}>
              Filter by Class (Optional)
              <select
                value={linkClassId}
                onChange={(e) => setLinkClassId(e.target.value)}
                disabled={!linkBranchId || linkClassesLoading}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 7,
                  border: '1px solid #cbd5e1', fontSize: 13, marginTop: 4,
                  background: !linkBranchId ? '#f1f5f9' : '#fff',
                  cursor: !linkBranchId ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">{!linkBranchId ? 'Select Branch First' : linkClassesLoading ? 'Loading...' : 'All Classes'}</option>
                {linkBranchClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.section ? `(${c.section})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label style={{ marginTop: '12px' }}>
            Search & Select Student
            <input
              type="text"
              placeholder="🔍 Search student by name or ID..."
              value={studentSearchQuery}
              onChange={(e) => setStudentSearchQuery(e.target.value)}
              style={{
                padding: '9px 12px',
                borderRadius: '8px',
                border: '1.5px solid #7c3aed',
                fontSize: '14px',
                width: '100%',
                marginTop: '4px',
                marginBottom: '8px',
                boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.1)'
              }}
            />
            <select
              value={mapping.student_id}
              onChange={(e) => setMapping((p) => ({ ...p, student_id: e.target.value }))}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select student ({filteredStudentsForLink.length} matches) --</option>
              {filteredStudentsForLink.map((student) => (
                <option key={student.id} value={student.id}>
                  🎓 {student.full_name} ({student.student_id || 'ID N/A'}) {student.class_grade ? `— Class ${student.class_grade}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label style={{ marginTop: '12px' }}>
            Relation (e.g. Father, Mother, Parent)
            <input
              placeholder="Relation"
              value={mapping.relation}
              onChange={(e) => setMapping((p) => ({ ...p, relation: e.target.value }))}
            />
          </label>

          <div className="modal-actions" style={{ marginTop: '20px' }}>
            <button type="button" className="secondary-button" onClick={() => setIsLinkOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Link Student</button>
          </div>
        </form>
      </Modal>



      {loading ? (
        <div>Loading parents...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Linked Students</th>
                <th>Created</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredParents.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                    No parents found for the selected filter/search.
                  </td>
                </tr>
              ) : (
                filteredParents.map((p) => (
                  <tr key={p.id}>
                    <td
                      style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 500 }}
                      onClick={() => navigate(`/parents/${p.id}`)}
                    >
                      {p.name || '-'}
                    </td>
                    <td>{p.phone || '-'}</td>
                    <td>{p.email || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {p.linked_students && p.linked_students.length > 0 ? (
                          p.linked_students.map((st) => (
                            <span
                              key={st.id}
                              style={{
                                padding: '3px 8px 3px 10px',
                                background: '#e0e7ff',
                                color: '#3730a3',
                                border: '1px solid #c7d2fe',
                                borderRadius: 12,
                                fontSize: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 500,
                              }}
                            >
                              <span
                                onClick={() => navigate(`/students/${st.id}?from=parents`)}
                                style={{ cursor: 'pointer' }}
                                title={`Click to view profile of ${st.full_name}`}
                              >
                                🎓 <strong>{st.student_id}</strong> ({st.full_name})
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlinkStudent(st.id, p.id, st.full_name, p.name);
                                }}
                                style={{
                                  border: 'none',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  borderRadius: '50%',
                                  width: '18px',
                                  height: '18px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  marginLeft: '4px'
                                }}
                                title={`Unlink ${st.full_name} from ${p.name}`}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: 13 }}>No student linked</span>
                        )}
                      </div>
                    </td>
                    <td>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <button
                          type="button"
                          className="secondary-button"
                          style={{ padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                          onClick={() => openEditModal(p)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          style={{
                            padding: '4px 10px', fontSize: '12px', borderRadius: '6px',
                            border: '1px solid #c7d2fe', backgroundColor: '#e0e7ff', color: '#4338ca',
                            cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => openLinkModal(p)}
                          title={`Link a student to ${p.name}`}
                        >
                          🔗 Link Student
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
